import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  Platform,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { LinearGradient } from 'expo-linear-gradient';

import { GradientCtaButton } from '../ui';
import { useTheme } from '../../context/ThemeContext';
import { typographyTokens } from '../../styles/typographyTokens';

import { ACCENT_TEXT } from '../../styles/accentText';
/**
 * CORE-842 — approved user-facing fallback «Этот эфир сейчас недоступен».
 *
 * Replaces the technical dead-end (alert-circle «!» + raw backend `error`
 * string + «НАЗАД») shown when a Live-session opened from a share link can't
 * be rendered: cancelled, deleted, expired link, missing entity, or a BE
 * state where entry is impossible. One unified screen for every covered
 * reason — the reason is logged (Sentry), never shown to the user.
 *
 * Copy is the approved text from the ticket, verbatim. Composition follows the
 * approved LIGHT/DARK mockups: illustration → serif title → muted subtitle →
 * primary (gradient) + secondary (outlined) CTA → tertiary «На главную» with
 * decorative side lines. Presentational only; the caller wires navigation.
 */

/** Approved copy — exported so tests can assert it stays verbatim. */
export const LIVE_UNAVAILABLE_COPY = {
  title: 'Этот эфир сейчас недоступен',
  // Adaptive line-wrapping is allowed by the ticket; the text itself is fixed.
  subtitle:
    'Возможно, встреча была отменена, завершилась или ссылка устарела. ' +
    'Вы можете выбрать другой эфир или перейти к практикам.',
  primary: 'Выбрать другой эфир',
  secondary: 'Перейти к практикам',
  tertiary: 'На главную',
} as const;

export interface LiveUnavailableScreenProps {
  /** Primary CTA → actual list of Live-sessions / эфиров. */
  onChooseLive: () => void;
  /** Secondary CTA → practices library. */
  onGoPractices: () => void;
  /** Tertiary action → home. */
  onGoHome: () => void;
}

export const LiveUnavailableScreen: React.FC<LiveUnavailableScreenProps> = ({
  onChooseLive,
  onGoPractices,
  onGoHome,
}) => {
  const { colors, isDark } = useTheme();
  const { width } = useWindowDimensions();
  // Below this the two CTAs stack vertically (primary stays first) and the card
  // takes the available width with safe side gutters — no horizontal scroll.
  const isNarrow = width < 560;

  const cardGlow =
    Platform.OS === 'web'
      ? ({
          boxShadow: isDark
            ? '0 30px 90px rgba(0,0,0,0.45), 0 0 60px rgba(233,130,69,0.08)'
            : '0 30px 90px rgba(233,130,69,0.14)',
        } as any)
      : { elevation: 8 };

  return (
    <View
      testID="live-unavailable"
      style={[styles.root, { backgroundColor: colors.pageBg }]}
    >
      <View
        style={[
          styles.card,
          {
            backgroundColor: colors.surface,
            borderColor: isDark ? 'rgba(233,130,69,0.20)' : 'rgba(233,130,69,0.16)',
          },
          cardGlow,
          isNarrow && styles.cardNarrow,
        ]}
      >
        {/* Illustration — glow disc + soft clouds + broadcast icon. Vector
            approximation of the approved mockup (no bespoke asset shipped). */}
        <View style={styles.illustration} accessibilityElementsHidden>
          <LinearGradient
            colors={
              isDark
                ? ['rgba(233,130,69,0.42)', 'rgba(244,196,83,0.10)', 'rgba(23,24,28,0)']
                : ['rgba(255,214,170,0.9)', 'rgba(255,236,214,0.55)', 'rgba(250,250,248,0)']
            }
            start={{ x: 0.5, y: 0.15 }}
            end={{ x: 0.5, y: 1 }}
            style={styles.glowDisc}
          >
            <Ionicons
              name="radio-outline"
              size={44}
              color={isDark ? '#F4C453' : ACCENT_TEXT}
            />
          </LinearGradient>
          <Ionicons
            name="cloud"
            size={26}
            color={isDark ? 'rgba(244,196,83,0.22)' : 'rgba(233,130,69,0.18)'}
            style={styles.cloudLeft}
          />
          <Ionicons
            name="cloud"
            size={20}
            color={isDark ? 'rgba(244,196,83,0.18)' : 'rgba(233,130,69,0.14)'}
            style={styles.cloudRight}
          />
        </View>

        <Text style={[styles.title, { color: colors.textPrimary }]}>
          {LIVE_UNAVAILABLE_COPY.title}
        </Text>
        <Text style={[styles.subtitle, { color: colors.textMuted }]}>
          {LIVE_UNAVAILABLE_COPY.subtitle}
        </Text>

        <View style={[styles.ctaRow, isNarrow && styles.ctaColumn]}>
          <GradientCtaButton
            testID="live-unavailable-primary"
            onPress={onChooseLive}
            leftIcon="radio-outline"
            showArrow={false}
            style={[styles.primaryBtn, isNarrow && styles.fullWidth]}
          >
            {LIVE_UNAVAILABLE_COPY.primary}
          </GradientCtaButton>

          <TouchableOpacity
            testID="live-unavailable-secondary"
            onPress={onGoPractices}
            activeOpacity={0.85}
            accessibilityRole="button"
            style={[
              styles.secondaryBtn,
              { borderColor: colors.solar },
              isNarrow && styles.fullWidth,
            ]}
          >
            <Ionicons name="flower-outline" size={20} color={ACCENT_TEXT} />
            <Text style={[styles.secondaryText, { color: ACCENT_TEXT }]} numberOfLines={1}>
              {LIVE_UNAVAILABLE_COPY.secondary}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Tertiary «На главную» — not a third big button; thin decorative
            lines flank it per the mockup. */}
        <View style={styles.tertiaryRow}>
          <View style={[styles.rule, { backgroundColor: colors.border }]} />
          <TouchableOpacity
            testID="live-unavailable-home"
            onPress={onGoHome}
            activeOpacity={0.7}
            accessibilityRole="button"
            style={styles.tertiaryBtn}
          >
            <Ionicons name="home-outline" size={16} color={colors.textMuted} />
            <Text style={[styles.tertiaryText, { color: colors.textMuted }]}>
              {LIVE_UNAVAILABLE_COPY.tertiary}
            </Text>
          </TouchableOpacity>
          <View style={[styles.rule, { backgroundColor: colors.border }]} />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingVertical: 32,
  },
  card: {
    width: '100%',
    maxWidth: 560,
    borderRadius: 28,
    borderWidth: 1,
    paddingHorizontal: 40,
    paddingVertical: 44,
    alignItems: 'center',
  },
  cardNarrow: {
    paddingHorizontal: 22,
    paddingVertical: 32,
  },
  illustration: {
    width: 168,
    height: 140,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 22,
  },
  glowDisc: {
    width: 128,
    height: 128,
    borderRadius: 64,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cloudLeft: {
    position: 'absolute',
    left: 6,
    bottom: 26,
  },
  cloudRight: {
    position: 'absolute',
    right: 10,
    bottom: 40,
  },
  title: {
    ...typographyTokens.uiH2,
    textAlign: 'center',
    marginBottom: 12,
  },
  subtitle: {
    ...typographyTokens.body,
    textAlign: 'center',
    maxWidth: 420,
    marginBottom: 28,
  },
  // CORE-853 — утверждённый текст CORE-842 не помещается в утверждённый ряд.
  // Замерено на dev (viewport 1440): карточка 560 при paddingHorizontal 40 даёт
  // ряду 480, а двум кнопкам в натуральную величину нужно 546 (268 + 14 + 264).
  // Ряд ужимал ПЕРВУЮ кнопку (flexShrink), и её `numberOfLines={1}` резал
  // «Выбрать другой эфир» до «Выбрать др…» — 190px текста в поле 119px.
  // Утверждённый текст пользователю не показывался целиком.
  //
  // Ширину карточки и сам текст не трогаем — и то и другое утверждено. Даём
  // ряду перенос: где кнопки помещаются, композиция макета та же; где нет —
  // они встают столбиком, то есть во ВТОРУЮ раскладку, которую этот же дизайн
  // уже описывает для узких экранов (`isNarrow && ctaColumn`). Отклонение
  // названо в тикете, а не подобрано молча.
  ctaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 14,
    width: '100%',
  },
  ctaColumn: {
    flexDirection: 'column',
  },
  fullWidth: {
    width: '100%',
    alignSelf: 'stretch',
  },
  primaryBtn: {
    // Не ужимать: перенос ряда выше делает это лишним, а сжатие резало текст.
    flexShrink: 0,
  },
  secondaryBtn: {
    minHeight: 62,
    borderRadius: 31,
    borderWidth: 1.5,
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : null),
  },
  secondaryText: {
    ...typographyTokens.buttonSecondary,
  },
  tertiaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    marginTop: 26,
    width: '100%',
    maxWidth: 360,
  },
  rule: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
  },
  tertiaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : null),
  },
  tertiaryText: {
    ...typographyTokens.label,
  },
});

export default LiveUnavailableScreen;
